import { useCallback, useEffect, useState } from "react";
import { PlayCircle, Scissors, CopyPlus, Check, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, fileUrl } from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const LEVEL = {
  green: { dot: "bg-emerald-400", text: "Smooth join", tone: "text-emerald-300" },
  amber: { dot: "bg-amber-400", text: "Noticeable change", tone: "text-amber-300" },
  red: { dot: "bg-red-500", text: "Big jump", tone: "text-red-400" },
  ok: { dot: "bg-slate-400", text: "Marked as intended", tone: "text-slate-300" },
};

export const useJoins = (epId, onlyApproved, signature) => {
  const [data, setData] = useState({ pending: 0, joins: [], inner: [] });
  const load = useCallback(async () => {
    try { const { data: d } = await http.get(`/episodes/${epId}/joins`, { params: { approved_only: onlyApproved } }); setData(d); } catch { /* keep last */ }
  }, [epId, onlyApproved]);
  useEffect(() => { load(); }, [load, signature]);
  useEffect(() => {
    if (!data.pending) return undefined;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [data, load]);
  return { ...data, reload: load };
};

export const JoinsSummary = ({ pending, joins, inner = [] }) => {
  const isBad = (j) => j.level === "amber" || j.level === "red";
  const bad = joins.filter(isBad).length, badIn = inner.filter(isBad).length;
  const parts = [bad && `${bad} between scenes`, badIn && `${badIn} inside scene${badIn > 1 ? "s" : ""}`].filter(Boolean);
  return (
    <span className="font-mono text-[11px]" data-testid="joins-summary">
      {pending > 0 ? <span className="text-violet-300">Checking joins… {pending} clip{pending > 1 ? "s" : ""} left</span>
        : parts.length ? <span className="text-amber-300">To check: {parts.join(" · ")}</span>
        : joins.length + inner.length ? <span className="text-emerald-300">All {joins.length + inner.length} joins smooth</span> : null}
    </span>
  );
};

export const InnerMarker = ({ j, left, onPlay, onCut, onFirst, onSecond, onRetake, onMark }) => {
  const [open, setOpen] = useState(false);
  const L = LEVEL[j.level];
  const warn = j.level === "amber" || j.level === "red";
  const act = (fn) => () => { setOpen(false); fn(); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={`inner-marker-${j.number}`} title={`Inside scene ${j.number} at ${j.t}s: ${L.text} (${j.score}% match)`} style={{ left: `${left}%` }}
          className={`absolute -top-1.5 -translate-x-1/2 z-10 rotate-45 ring-2 ring-[#0B0D14] transition-transform hover:scale-125 ${L.dot} ${warn ? "w-3 h-3" : "w-2 h-2"}`} />
      </PopoverTrigger>
      <PopoverContent side="bottom" collisionPadding={8} data-testid={`inner-popover-${j.number}`} className="w-80 p-3 bg-[#11141E] border-[#2A3245] space-y-3">
        <div className="flex items-center gap-2 text-sm">
          <span className={`w-2.5 h-2.5 rotate-45 ${L.dot}`} />
          <span className="font-medium">Inside scene {j.number} · {j.t}s</span>
          <span className={`ml-auto font-mono text-[11px] ${L.tone}`} data-testid="inner-score">{L.text} · {j.score}%</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {[[j.before, "End of shot 1"], [j.after, "Start of shot 2"]].map(([p, label]) => (
            <figure key={label} className="space-y-1">
              <img src={fileUrl(p)} alt={label} className="w-full aspect-video object-cover rounded border border-[#2A3245]" />
              <figcaption className="font-mono text-[9px] text-slate-500">{label.toUpperCase()}</figcaption>
            </figure>
          ))}
        </div>
        {warn && <div className="text-[11px] text-slate-500">If this is just a normal camera-angle change (e.g. close-up to wide shot), mark it as intended.</div>}
        <div className="space-y-0.5">
          <Fix t="inner-play-btn" icon={PlayCircle} onClick={act(onPlay)}>Play the join (2s before → after)</Fix>
          <Fix t="inner-cut-btn" icon={Scissors} onClick={act(onCut)}>Cut out the jump ({j.cut[0]}s → {j.cut[1]}s, with a quick cross-fade)</Fix>
          <Fix t="inner-first-btn" icon={Scissors} onClick={act(onFirst)}>Keep only the first half (to {j.first_end}s)</Fix>
          <Fix t="inner-second-btn" icon={Scissors} onClick={act(onSecond)}>Keep only the second half (from {j.second_start}s)</Fix>
          <Fix t="inner-retake-btn" icon={CopyPlus} onClick={act(onRetake)}>Make another take of scene {j.number} in Studio</Fix>
          {j.level === "ok"
            ? <Fix t="inner-unmark-btn" icon={Undo2} onClick={act(() => onMark(false))}>Check this join again</Fix>
            : warn && <Fix t="inner-mark-ok-btn" icon={Check} onClick={act(() => onMark(true))}>This change is intended</Fix>}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const Fix = ({ t, icon: Icon, children, onClick }) => (
  <button data-testid={t} onClick={onClick} className="w-full flex items-center gap-2 text-left text-xs px-2.5 py-1.5 rounded hover:bg-[#212738] transition-colors">
    <Icon className="w-3.5 h-3.5 text-cyan-400 shrink-0" />{children}
  </button>
);

export const JoinMarker = ({ j, left, onPlay, onTrimStart, onTrimEnd, onRetake, onMark }) => {
  const [open, setOpen] = useState(false);
  const L = LEVEL[j.level];
  const warn = j.level === "amber" || j.level === "red";
  const act = (fn) => () => { setOpen(false); fn(); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={`join-marker-${j.a_number}-${j.b_number}`} title={`Scene ${j.a_number} → ${j.b_number}: ${L.text} (${j.score}% match)`}
          style={{ left: `${left}%` }}
          className={`absolute -top-1.5 -translate-x-1/2 z-10 rounded-full ring-2 ring-[#0B0D14] transition-transform hover:scale-125 ${L.dot} ${warn ? "w-3.5 h-3.5" : "w-2.5 h-2.5"}`} />
      </PopoverTrigger>
      <PopoverContent side="bottom" collisionPadding={8} data-testid={`join-popover-${j.a_number}-${j.b_number}`} className="w-80 p-3 bg-[#11141E] border-[#2A3245] space-y-3">
        <div className="flex items-center gap-2 text-sm">
          <span className={`w-2.5 h-2.5 rounded-full ${L.dot}`} />
          <span className="font-medium">Scene {j.a_number} → {j.b_number}</span>
          <span className={`ml-auto font-mono text-[11px] ${L.tone}`} data-testid="join-score">{L.text} · {j.score}%</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {[[j.a_last, `End of ${j.a_number}`], [j.b_first, `Start of ${j.b_number}`]].map(([p, label]) => (
            <figure key={label} className="space-y-1">
              <img src={fileUrl(p)} alt={label} className="w-full aspect-video object-cover rounded border border-[#2A3245]" />
              <figcaption className="font-mono text-[9px] text-slate-500">{label.toUpperCase()}</figcaption>
            </figure>
          ))}
        </div>
        <div className="space-y-0.5">
          <Fix t="join-play-btn" icon={PlayCircle} onClick={act(onPlay)}>Play the join (2s before → after)</Fix>
          <Fix t="join-trim-start-btn" icon={Scissors} onClick={act(onTrimStart)}>
            Trim the start of scene {j.b_number}{j.start_cut ? ` (from its first angle change at ${j.start_cut}s)` : " (about 1s in)"}
          </Fix>
          {j.end_cut && (
            <Fix t="join-trim-end-btn" icon={Scissors} onClick={act(onTrimEnd)}>Trim the end of scene {j.a_number} (at its last angle change, {j.end_cut}s)</Fix>
          )}
          <Fix t="join-retake-btn" icon={CopyPlus} onClick={act(onRetake)}>Make another take of scene {j.b_number} in Studio</Fix>
          {j.level === "ok"
            ? <Fix t="join-unmark-btn" icon={Undo2} onClick={act(() => onMark(false))}>Check this join again</Fix>
            : warn && <Fix t="join-mark-ok-btn" icon={Check} onClick={act(() => onMark(true))}>This jump is intended (new place / time jump)</Fix>}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const markJoin = async (epId, j, ok, reload) => {
  const body = j.scene_id ? { inner_id: j.scene_id, ok } : { a_id: j.a_id, b_id: j.b_id, ok };
  try { await http.post(`/episodes/${epId}/joins/ok`, body); await reload(); toast.success(ok ? "Marked as intended" : "Join will be checked again"); }
  catch (e) { toast.error(errMsg(e)); }
};
